import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Iconify from '../../components/Iconify';
import Label from '../../components/Label';
import { setAuthToken, setAuthUser } from '../../redux/reducers/authSlice';
import { authApiCall } from '../../services/http';
import { endpoints } from '../../config';
import { stripeSubscriptionUrl, subscriptionStatus } from '../../helpers/subscriptions';
import SubscriptionDetailsDialog from './SubscriptionDetailsDialog';

// Estados terminales: una sub en estos ya no puede entrar en conflicto con una nueva.
// Ojo: NO existe un status `trialing` en el modelo — un trial vivo viaja como `active`
// (normalmente manual y con currentPeriodEnd), así que el criterio es "no terminal".
const TERMINAL_STATUSES = ['canceled', 'expired'];

const fDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');

const fEur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

const priceLabel = (sub) => {
  if (sub.pricePerUserEur != null && sub.pricePerUserEur !== '') return `${fEur(sub.pricePerUserEur)}/usuario`;
  if (sub.flatPriceEur != null && sub.flatPriceEur !== '') return `${fEur(sub.flatPriceEur)} cerrado`;
  return 'Sin precio';
};

UserActiveSubscriptionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Sub que acabamos de crear/asignar: se excluye del listado, no es un conflicto consigo misma.
  excludeSubscriptionId: PropTypes.string,
  // Texto que explica de dónde venimos ("Has creado una sub para el usuario #12").
  contextMessage: PropTypes.string,
};

/**
 * Chequeo post-atribución: tras meter un usuario en una sub, enseña qué OTRAS subs vivas
 * tiene como titular individual, para cazar solapes (p. ej. un trial anterior sin cancelar)
 * y poder entrar a editarlas/cancelarlas sin salir del flujo.
 *
 * Limitación heredada del endpoint: `/subscriptions/list?userId=` filtra por TITULAR de la sub,
 * no por ocupante de seat. Una sub de equipo en la que el usuario solo ocupa asiento no aparece.
 */
export default function UserActiveSubscriptionsDialog({
  open,
  onClose,
  userId,
  excludeSubscriptionId,
  contextMessage,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsSub, setDetailsSub] = useState(null);

  useEffect(() => {
    if (!open || !userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setItems([]);
    setDetailsSub(null);

    authApiCall({
      method: 'GET',
      endpoint: endpoints.subscriptions,
      path: `/list?ownerType=individual&userId=${encodeURIComponent(
        userId
      )}&pageSize=50&orderBy=createdAt&orderDir=DESC`,
      navigate,
      dispatch,
      setAuthUser,
      setAuthToken,
      authToken,
      redirectRoute: null,
    })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.items || [];
        setItems(list.filter((s) => s.id !== excludeSubscriptionId && !TERMINAL_STATUSES.includes(s.status)));
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.error || e?.message || 'Error cargando las suscripciones del usuario');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, userId, excludeSubscriptionId, authToken]);

  // Tras editar (p. ej. pasar a canceled) reflejamos el cambio y sacamos la sub de la lista
  // si ya ha quedado en un estado terminal: el conflicto está resuelto.
  const handleSaved = (updated) => {
    setItems((prev) =>
      prev
        .map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
        .filter((s) => !TERMINAL_STATUSES.includes(s.status))
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Otras suscripciones de este usuario</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {contextMessage && (
              <Typography variant="caption" color="text.secondary">
                {contextMessage}
              </Typography>
            )}

            {loading && (
              <Stack alignItems="center" py={3}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && items.length === 0 && (
              <Alert severity="success">
                Este usuario no tiene ninguna otra suscripción viva como titular. Sin solapes.
              </Alert>
            )}

            {!loading && !error && items.length > 0 && (
              <>
                <Alert severity="warning">
                  {items.length === 1
                    ? 'Este usuario ya tenía otra suscripción viva. Revísala por si hay que cancelarla.'
                    : `Este usuario ya tenía ${items.length} suscripciones vivas. Revísalas por si hay que cancelarlas.`}
                </Alert>

                <Stack divider={<Divider />} spacing={0}>
                  {items.map((s) => {
                    const st = subscriptionStatus(s);
                    return (
                      <Stack
                        key={s.id}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        py={1.5}
                      >
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Label color={st.color}>{st.label}</Label>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }} color="text.secondary">
                              {String(s.id).slice(0, 8)}
                            </Typography>
                            {s.stripeSubscriptionId ? (
                              <Tooltip title="Ver en Stripe">
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={stripeSubscriptionUrl(s.stripeSubscriptionId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <Iconify icon="eva:external-link-outline" width={16} height={16} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Label color="default">Manual</Label>
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {`Creada ${fDate(s.createdAt)} · Fin de periodo ${fDate(s.currentPeriodEnd)} · ${priceLabel(
                              s
                            )}`}
                          </Typography>
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Iconify icon="eva:edit-2-outline" />}
                          onClick={() => setDetailsSub(s)}
                        >
                          Editar
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>

                <Typography variant="caption" color="text.disabled">
                  Solo se listan las subs de las que el usuario es titular individual. Las de equipo en las que
                  únicamente ocupa un asiento no aparecen aquí.
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <SubscriptionDetailsDialog
        open={Boolean(detailsSub)}
        onClose={() => setDetailsSub(null)}
        subscription={detailsSub}
        onSaved={handleSaved}
      />
    </>
  );
}
